import type { Project, Workflow, WorkspaceUsage } from "@functions/state/types";
import { isEnterprisePlan } from "../routes/workspace/utils";
import type { RapidPromptData, TextPromptItem } from "@functions/rapid/schemas";

// Removed RapidOptInConfig as it's no longer needed

// Helper functions

export function getPromptObjects(prompts: RapidPromptData): string[] {
    // Text prompting is the only supported mode
    const objects = prompts.items.map((item) => (item as TextPromptItem).text);
    return [...new Set(objects)];
}

function isLinkedToRapidData(resource: { rapidDataId?: string }) {
    return !!resource?.rapidDataId && typeof resource.rapidDataId === "string";
}

// Removed bucketing and config loading logic as we no longer default to Rapid

// Exported functions

export function isRapidProject(project: Project) {
    return isLinkedToRapidData(project);
}

export function isRapidWorkflow(workflow: Workflow) {
    return isLinkedToRapidData(workflow);
}

export function canOptInToRapid(usage: WorkspaceUsage) {
    // Override for user admins: they can opt in to rapid
    if (typeof window !== "undefined" && window.user?.isAdmin) return true;

    return !isEnterprisePlan(usage);
}

// Removed shouldDefaultToRapid function - we always default to Traditional now

type RapidExampleVideo = {
    srcSmall: string;
    srcLarge: string;
    originalSrc: string;
    poster: string;
    posterSmall: string;
    prompt: string;
    title: string;
};

export const RAPID_EXAMPLE_VIDEOS: RapidExampleVideo[] = [
    {
        srcSmall: "/videos/home/annotated_aidogs_360.webm",
        srcLarge: "/videos/home/annotated_aidogs.webm",
        originalSrc: "/videos/home/raw_aidogs.mp4",
        poster: "/videos/home/annotated_aidogs_poster.jpg",
        posterSmall: "/videos/home/annotated_aidogs_poster_360.jpg",
        prompt: "dog",
        title: "AI Dogs"
    },
    {
        srcSmall: "/videos/home/annotated_intersection_360.webm",
        srcLarge: "/videos/home/annotated_intersection.webm",
        originalSrc: "/videos/home/raw_intersection.mp4",
        poster: "/videos/home/annotated_intersection_poster.jpg",
        posterSmall: "/videos/home/annotated_intersection_poster_360.jpg",
        prompt: "car",
        title: "Cars intersection"
    },
    {
        srcSmall: "/videos/home/annotated_bunnies_360.webm",
        srcLarge: "/videos/home/annotated_bunnies.webm",
        originalSrc: "/videos/home/raw_bunnies.mp4",
        poster: "/videos/home/annotated_bunnies_poster.jpg",
        posterSmall: "/videos/home/annotated_bunnies_poster_360.jpg",
        prompt: "bunny",
        title: "Bunnies on trampoline"
    },
    {
        srcSmall: "/videos/home/annotated_soccer_360.webm",
        srcLarge: "/videos/home/annotated_soccer.webm",
        originalSrc: "/videos/home/raw_soccer.mp4",
        poster: "/videos/home/annotated_soccer_poster.jpg",
        posterSmall: "/videos/home/annotated_soccer_poster_360.jpg",
        prompt: "ball",
        title: "Soccer game"
    },
    {
        srcSmall: "/videos/home/annotated_waterballoons_360.webm",
        srcLarge: "/videos/home/annotated_waterballoons.webm",
        originalSrc: "/videos/home/raw_waterballoons.mp4",
        poster: "/videos/home/annotated_waterballoons_poster.jpg",
        posterSmall: "/videos/home/annotated_waterballoons_poster_360.jpg",
        prompt: "balloon",
        title: "Water balloons"
    },
    {
        srcSmall: "/videos/home/annnotated_katy_2_360.webm",
        srcLarge: "/videos/home/annnotated_katy_2.webm",
        originalSrc: "/videos/home/raw_katy.mp4",
        poster: "/videos/home/annnotated_katy_2_poster.jpg",
        posterSmall: "/videos/home/annnotated_katy_2_poster_360.jpg",
        prompt: "flower, person",
        title: "Katy Perry in space"
    },
    {
        srcSmall: "/videos/home/annotated_aerialfactory_360.webm",
        srcLarge: "/videos/home/annotated_aerialfactory.webm",
        originalSrc: "/videos/home/raw_aerialfactory.mp4",
        poster: "/videos/home/annotated_aerialfactory_poster.jpg",
        posterSmall: "/videos/home/annotated_aerialfactory_poster_360.jpg",
        prompt: "worker",
        title: "Aerial factory"
    },
    {
        srcSmall: "/videos/home/annotated_anduril_360.webm",
        srcLarge: "/videos/home/annotated_anduril.webm",
        originalSrc: "/videos/home/raw_anduril.mp4",
        poster: "/videos/home/annotated_anduril_poster.jpg",
        posterSmall: "/videos/home/annotated_anduril_poster_360.jpg",
        prompt: "drone",
        title: "Anduril"
    },
    {
        srcSmall: "/videos/home/annotated_baguette_360.webm",
        srcLarge: "/videos/home/annotated_baguette.webm",
        originalSrc: "/videos/home/raw_baguette.mp4",
        poster: "/videos/home/annotated_baguette_poster.jpg",
        posterSmall: "/videos/home/annotated_baguette_poster_360.jpg",
        prompt: "baguette",
        title: "Baguette"
    },
    {
        srcSmall: "/videos/home/annotated_basketball_360.webm",
        srcLarge: "/videos/home/annotated_basketball.webm",
        originalSrc: "/videos/home/raw_basketball.mp4",
        poster: "/videos/home/annotated_basketball_poster.jpg",
        posterSmall: "/videos/home/annotated_basketball_poster_360.jpg",
        prompt: "basketball",
        title: "Basketball"
    },
    {
        srcSmall: "/videos/home/annotated_basketball2_360.webm",
        srcLarge: "/videos/home/annotated_basketball2.webm",
        originalSrc: "/videos/home/raw_basketball2.mp4",
        poster: "/videos/home/annotated_basketball2_poster.jpg",
        posterSmall: "/videos/home/annotated_basketball2_poster_360.jpg",
        prompt: "basketball",
        title: "Basketball"
    },
    {
        srcSmall: "/videos/home/annotated_canoe_360.webm",
        srcLarge: "/videos/home/annotated_canoe.webm",
        originalSrc: "/videos/home/raw_canoe.mp4",
        poster: "/videos/home/annotated_canoe_poster.jpg",
        posterSmall: "/videos/home/annotated_canoe_poster_360.jpg",
        prompt: "canoe",
        title: "Canoe"
    },
    {
        srcSmall: "/videos/home/annotated_cars_360.webm",
        srcLarge: "/videos/home/annotated_cars.webm",
        originalSrc: "/videos/home/raw_cars.mp4",
        poster: "/videos/home/annotated_cars_poster.jpg",
        posterSmall: "/videos/home/annotated_cars_poster_360.jpg",
        prompt: "car",
        title: "Cars"
    },
    {
        srcSmall: "/videos/home/annotated_flyover_360.webm",
        srcLarge: "/videos/home/annotated_flyover.webm",
        originalSrc: "/videos/home/raw_flyover.mp4",
        poster: "/videos/home/annotated_flyover_poster.jpg",
        posterSmall: "/videos/home/annotated_flyover_poster_360.jpg",
        prompt: "airplane",
        title: "Airplane Flyover"
    },
    {
        srcSmall: "/videos/home/annotated_ghostx_360.webm",
        srcLarge: "/videos/home/annotated_ghostx.webm",
        originalSrc: "/videos/home/raw_ghostx.mp4",
        poster: "/videos/home/annotated_ghostx_poster.jpg",
        posterSmall: "/videos/home/annotated_ghostx_poster_360.jpg",
        prompt: "helicopter",
        title: "GhostX"
    },
    {
        srcSmall: "/videos/home/annotated_randy_360.webm",
        srcLarge: "/videos/home/annotated_randy.webm",
        originalSrc: "/videos/home/raw_randy.mp4",
        poster: "/videos/home/annotated_randy_poster.jpg",
        posterSmall: "/videos/home/annotated_randy_poster_360.jpg",
        prompt: "truck, worker",
        title: "Randy"
    },
    {
        srcSmall: "/videos/home/annotated_robotarm_360.webm",
        srcLarge: "/videos/home/annotated_robotarm.webm",
        originalSrc: "/videos/home/raw_robotarm.mp4",
        poster: "/videos/home/annotated_robotarm_poster.jpg",
        posterSmall: "/videos/home/annotated_robotarm_poster_360.jpg",
        prompt: "orange, brown",
        title: "Robot arm"
    },
    {
        srcSmall: "/videos/home/annotated_sushi_360.webm",
        srcLarge: "/videos/home/annotated_sushi.webm",
        originalSrc: "/videos/home/raw_sushi.mp4",
        poster: "/videos/home/annotated_sushi_poster.jpg",
        posterSmall: "/videos/home/annotated_sushi_poster_360.jpg",
        prompt: "sushi",
        title: "Sushi"
    }
];
